// ── useTicketManagement — 상품·카테고리·유효기간 상태 & API ───────────────────
// (피드백 옵션 상태·API 는 미팅지도 관리 useMeetingMapSettings 로 이관 — 2026-09-01)
import { useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { todayKST } from '../../../utils/date';

export function useTicketManagement() {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  // ── 상품 목록 ──────────────────────────────────────────────────────────────
  const [tickets,            setTickets]            = useState([]);
  const [ticketLoading,      setTicketLoading]      = useState(false);
  const [newTicketName,      setNewTicketName]      = useState('');
  const [activeAddCat,       setActiveAddCat]       = useState(null);   // 추가 폼이 열린 카테고리명
  const [editTicketId,       setEditTicketId]       = useState(null);
  const [editTicketName,     setEditTicketName]     = useState('');
  const [editTicketCategory, setEditTicketCategory] = useState('');
  const [confirmTicketId,    setConfirmTicketId]    = useState(null);
  const [editWeightId,       setEditWeightId]       = useState(null);
  const [editWeightValue,    setEditWeightValue]    = useState('');
  const [editWeightDate,     setEditWeightDate]     = useState(todayKST());
  const [editWeightMemo,     setEditWeightMemo]     = useState('');

  // ── 카테고리 ───────────────────────────────────────────────────────────────
  const [categories,           setCategories]           = useState([]);
  const [editCatId,            setEditCatId]            = useState(null);
  const [editCatName,          setEditCatName]          = useState('');
  const [confirmDeleteCatId,   setConfirmDeleteCatId]   = useState(null);

  // ── 유효기간 { [ticketId]: { val: string, editing: boolean } } ─────────────
  const [ticketValidityMap, setTicketValidityMap] = useState({});

  // ── 카테고리 API ────────────────────────────────────────────────────────────
  async function fetchCategories() {
    const r = await apiClient.get('/api/admin/product-categories');
    if (!r.ok) { showToast('카테고리 목록을 불러오지 못했습니다.', 'error'); return; }
    setCategories(Array.isArray(r.data?.data) ? r.data.data : []);
  }

  async function saveCategoryName(catId) {
    const name = editCatName.trim();
    if (!name) { showToast('카테고리명을 입력해 주세요.', 'error'); return; }
    setSaving(true);
    const r = await apiClient.put(`/api/admin/product-categories/${catId}`, { name });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '수정 실패', 'error'); return; }
    setEditCatId(null);
    await Promise.all([fetchCategories(), fetchTickets()]);
  }

  async function deleteCategory(catId) {
    setSaving(true);
    const r = await apiClient.del(`/api/admin/product-categories/${catId}`);
    setSaving(false);
    setConfirmDeleteCatId(null);
    if (!r.ok) { showToast(r.data?.message || '삭제 실패', 'error'); return; }
    showToast('카테고리가 삭제됐습니다.', 'success');
    await fetchCategories();
  }

  // 카테고리 일일콜 표시 방식 토글 (상품 ↔ 카테고리명)
  async function toggleCategoryCallDisplay(catId, currentIsCategory) {
    setSaving(true);
    const r = await apiClient.patch(`/api/admin/product-categories/${catId}/call-display`, { is_category: !currentIsCategory });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    showToast('일일콜 표시 방식이 변경됐습니다.', 'success');
    fetchCategories();
  }

  // 기본 상품 설정 — productId가 빈 문자열이면 해제
  async function saveDefaultProduct(catId, productId) {
    const pid = productId ? Number(productId) : null;
    setSaving(true);
    const r = await apiClient.put(`/api/admin/product-categories/${catId}/default-product`, { product_id: pid });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    fetchCategories();
    showToast('기본 상품이 설정되었습니다.', 'success');
  }

  // ── 상품 API ────────────────────────────────────────────────────────────────
  async function fetchTickets() {
    setTicketLoading(true);
    const r = await apiClient.get('/api/admin/ticket-types');
    setTicketLoading(false);
    if (!r.ok) { showToast('상품 목록을 불러오지 못했습니다.', 'error'); return; }
    const active = (r.data?.data || []).filter((t) => t.is_active);
    setTickets(active);
    // 편집 중인 항목은 유지, 나머지는 DB 값으로 초기화
    setTicketValidityMap((prev) => {
      const next = {};
      active.forEach((t) => {
        next[t.id] = prev[t.id]?.editing
          ? prev[t.id]
          : { val: t.default_validity_months != null ? String(t.default_validity_months) : '', editing: false };
      });
      return next;
    });
  }

  async function addTicket() {
    const name = newTicketName.trim();
    if (!name || !activeAddCat) return;
    setSaving(true);
    const r = await apiClient.post('/api/admin/ticket-types', { name, category: activeAddCat });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '추가 실패', 'error'); return; }
    setNewTicketName('');
    setActiveAddCat(null);
    fetchTickets();
  }

  async function saveTicketEdit() {
    const name = editTicketName.trim();
    if (!name) { showToast('상품명을 입력해 주세요.', 'error'); return; }
    setSaving(true);
    const r = await apiClient.put(`/api/admin/ticket-types/${editTicketId}`, { name, category: editTicketCategory });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '수정 실패', 'error'); return; }
    setEditTicketId(null);
    fetchTickets();
  }

  function startEditWeight(ticket) {
    setEditWeightId(ticket.id);
    setEditWeightValue(String(ticket.current_weight ?? 1));
    setEditWeightDate(todayKST());
    setEditWeightMemo('');
    setEditTicketId(null);
    setConfirmTicketId(null);
  }

  function cancelEditWeight() {
    setEditWeightId(null);
    setEditWeightValue('');
    setEditWeightDate(todayKST());
    setEditWeightMemo('');
  }

  async function saveTicketWeight(ticketId) {
    const weight = Number(editWeightValue);
    if (!Number.isFinite(weight) || weight < 0) {
      showToast('환산장수는 0 이상 숫자로 입력해 주세요.', 'error');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(editWeightDate)) {
      showToast('적용 시작일을 선택해 주세요.', 'error');
      return;
    }
    setSaving(true);
    const r = await apiClient.put(`/api/admin/ticket-types/${ticketId}/weight`, {
      company_code: 'PINE',
      effective_from: editWeightDate,
      weight,
      memo: editWeightMemo,
    });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '환산장수 저장 실패', 'error'); return; }
    cancelEditWeight();
    await fetchTickets();
    showToast('환산장수가 저장되었습니다. 적용 시작일 이후 상황판, 직원 요약, 마케터 1:1 청구에 반영됩니다.', 'success');
  }

  async function deleteTicket(id) {
    setSaving(true);
    const r = await apiClient.del(`/api/admin/ticket-types/${id}`);
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '삭제 실패', 'error'); return; }
    setConfirmTicketId(null);
    fetchTickets();
  }

  // ── 유효기간 편집 헬퍼 ──────────────────────────────────────────────────────
  function startEditValidity(ticketId) {
    setTicketValidityMap((prev) => ({ ...prev, [ticketId]: { ...prev[ticketId], editing: true } }));
  }
  function cancelEditValidity(ticketId) {
    setTicketValidityMap((prev) => ({ ...prev, [ticketId]: { ...prev[ticketId], editing: false } }));
  }
  async function saveValidity(ticketId) {
    const cur = ticketValidityMap[ticketId];
    if (!cur) return;
    const months = cur.val === '' ? null : Number(cur.val);
    setSaving(true);
    const r = await apiClient.put(`/api/admin/ticket-types/${ticketId}/validity`, { months });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    const saved = r.data?.value;
    setTicketValidityMap((prev) => ({
      ...prev,
      [ticketId]: { val: saved != null ? String(saved) : '', editing: false },
    }));
    showToast('저장되었습니다.', 'success');
  }

  return {
    saving,
    // 상품
    tickets, ticketLoading, newTicketName, setNewTicketName,
    activeAddCat, setActiveAddCat,
    editTicketId, setEditTicketId, editTicketName, setEditTicketName,
    editTicketCategory, setEditTicketCategory,
    confirmTicketId, setConfirmTicketId,
    editWeightId, editWeightValue, setEditWeightValue,
    editWeightDate, setEditWeightDate,
    editWeightMemo, setEditWeightMemo,
    fetchTickets, addTicket, saveTicketEdit, deleteTicket,
    startEditWeight, cancelEditWeight, saveTicketWeight,
    // 카테고리
    categories,
    editCatId, setEditCatId, editCatName, setEditCatName,
    confirmDeleteCatId, setConfirmDeleteCatId,
    fetchCategories, saveCategoryName, deleteCategory, saveDefaultProduct,
    toggleCategoryCallDisplay,
    // 유효기간
    ticketValidityMap, setTicketValidityMap,
    startEditValidity, cancelEditValidity, saveValidity,
  };
}
